import { createAction, createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import * as api from '@codametrix/client-api';
import * as commonActions from './action-types';
import { authHandler } from '@codametrix/client-api';
import * as appServiceLines from '../core/service-lines';
import { authenticationChannel } from '../core/authentication-channel/authentication-channel';
import * as commonEnums from '../enums/index';
import * as commonActionUtils from './_action-utilities';
import * as cmxDateTime from '../core/date-time/cmx-date-time';

import { caseLookupError, caseSearchAvailable, fetchCases } from './launch-case-lookup';
import {
  fetchProcessInstance,
  getCasesWithEncounter,
  getDocumentsAsHtml,
  getFlagItems,
  getCCICustomClaimCrossCases,
  fetchOrderTypeStatus,
  getCodesWithEncounter,
  fetchRelatedCodes,
  fetchProviderRoleTypeConfigurations,
  fetchRelateCaseFilterDictionaries
} from './code-bench.actions';
import { loadCase, loadCaseFromURL } from './cases.action';

// util functions
import { loadXCaliber } from './xcaliber.actions';
import { fetchImprovementOptions } from './improvements.actions';
import { isIPC, isPathology, isSurgery, buildCaseDetailUrl } from '../core/service-lines';
import { isResourceAllowed } from '../core/amplify/utils/bench-editable';
import { shimContext } from './_action-utilities';
import { caseEditsActions } from './case-edits.actions';
import { navigationIntent } from './navigation-intent.action';
import {
  orchestrateTwoPhaseCaseDetailFetch,
  requestJSONWithCacheMeta,
  appendCaseDetailRefreshParam
} from '../core/case-accelerator';
import { runCaseDetailDiff } from '../core/case-detail-diff-runner';
import { isCaseDetailTwoPhaseEnabled } from '@codametrix/client-runtime';
import {
  caseDetailReconciliationApplied,
  caseDetailFreshnessWarning
} from './case-accelerator.actions';
import abortController from '../core/abort-controller';

export { navigationIntent } from './navigation-intent.action';
export type {
  NavigationIntent,
  NavigationIntentPayload
} from './navigation-intent.action';

import { relatedCasesSortablePageable } from '../core/amplify/utils/pagination-utils';
import { getRelatedCasesInitialFilters } from '../core/amplify/utils/common-filter-utils';

const { AmplifyAuthenticate, Launch, Case } = commonActions;

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

// --------------------- sync actions  --------------------- //

const userDetailsAvailable = createAction<CMxAPI.ContextAPI>(
  AmplifyAuthenticate.USER_DETAILS
);
const launchParamsAvailable = createAction<CMxAPI.LaunchParams>(
  Launch.PARAMS_AVAILABLE
);
const contextParams = createAction<any>(Launch.CONTEXT_PARAMS);
const hostCaseId = createAction<{ hostCaseId: number }>(Launch.PARENT_CASE);

const billingGroupAvailable = createAction<CMxAPI.PageableList<CMxAPI.Case>>(
  Launch.BILLING_GROUP
);
const caseRunAvailable = createAction<CMxAPI.CaseRun>(Launch.CASE_DETAIL);

const caseRunWithRefreshAvailable = createAction<CMxAPI.CaseRun>(
  Launch.CASE_DETAIL_WITH_REFRESH
);

const epicToken = createAction<string>(Launch.EPIC_TOKEN);

const selectCase = createAsyncThunk<
  CMxAPI.CaseRun,
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.CASE_SELECTED, async (caseRun, { dispatch }) => {
  const { path: slPath, caseDetailVersion } = appServiceLines.get();
  const url = `${slPath}/caseinstance/${caseRun.case_id}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  const withDetails = await api.requestJSON<CMxAPI.Case>(url);
  //
  const activeItem = withDetails.caseRuns[0];
  await loadCase(activeItem, dispatch);
  return activeItem;
});

type PopupLaunchParmas = {
  token: string;
  caseId: string;
  serviceLine: string;
};

const fetchContextJSON = async () => {
  const userDetailsResponse = await api.request('/contexts/v1');
  const contextJSON: CMxAPI.ContextAPI = await userDetailsResponse.json();
  return contextJSON;
};

type LoadFromUrlState = {
  login?: {
    auth?: {
      token?: string | null;
    };
  };
  ui?: {
    context?: CMx.Context;
  };
};

type LoadFromUrlDispatch = ThunkDispatch<LoadFromUrlState, unknown, Action>;

const getLoadedContext = (state: LoadFromUrlState): CMx.Context | null => {
  const context = state?.ui?.context;
  return context?.activeContext ? context : null;
};

const getStateAuthToken = (
  state: LoadFromUrlState
): string | null | undefined =>
  state?.login?.auth?.token ?? state?.ui?.context?.jwtToken;

const syncAuthTokenFromState = (state: LoadFromUrlState) => {
  if (authHandler.token) {
    return;
  }

  const token = getStateAuthToken(state);
  if (token) {
    authHandler.token = token;
  }
};

const tokenAvailable = createAsyncThunk<
  void,
  PopupLaunchParmas,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Launch.LOAD_CASE_WITH_TOKEN,
  async (launchParams: PopupLaunchParmas, { dispatch }) => {
    authHandler.token = launchParams.token;
    const sl = appServiceLines.determineServiceLineConfig({
      name: launchParams.serviceLine,
      caseIdentifier: `${launchParams.caseId}`
    });
    appServiceLines.set(sl);

    const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();

    dispatch(userDetailsAvailable(contextJSON));

    const { path: slPath, caseDetailVersion } = appServiceLines.get();
    const url = `${slPath}/caseinstance/${launchParams.caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
    const withDetails = await api.requestJSON<CMxAPI.Case>(url);
    //
    const activeItem = withDetails.caseRuns[0];
    const caseUrl = (await loadCase(activeItem, dispatch)).url;

    dispatch(caseRunAvailable(activeItem));
    dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: caseUrl }));
    return;
  }
);

const loadCaseFromId = createAsyncThunk<
  void,
  Pick<PopupLaunchParmas, 'caseId'>,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.LOAD_CASE_FROM_ID, async (launchParams, { dispatch }) => {
  const { path: slPath, caseDetailVersion } = appServiceLines.get();

  const url = `${slPath}/caseinstance/${launchParams.caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  const withDetails = await api.requestJSON<CMxAPI.Case>(url);
  //
  const activeItem = withDetails.caseRuns[0];
  await loadCase(activeItem, dispatch);
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line,
    caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
  });

  const caseId =
    serviceLineConfig.caseIdProp in activeItem
      ? activeItem[serviceLineConfig.caseIdProp]
      : activeItem.case_id;

  const sl = activeItem.service_line?.toLowerCase() ?? '';
  dispatch(caseRunAvailable(activeItem));
  
  const pathname = window.location.pathname;
  const matchWorklist = pathname.match(/^\/cmx\/(worklist|coding-quality)/);
  const basePath = matchWorklist ? matchWorklist[0] : '/cmx/worklist';
  
  dispatch(navigationIntent({ 
    intent: 'CASE_DETAIL', 
    url: `${basePath}/${sl}/case/${caseId}/case-run/${activeItem.process_instance_id}`,
    replace: true
  }));
  return;
});

type SimpleParams = {
  params: {
    [key: string]: string;
  };
};

const loadCaseRunWithRefresh = async (
  url: string,
  processInstanceIdProp: string,
  match: SimpleParams,
  dispatch: any
) => {
  if (!url) return;
  const urlWithRefresh = `${url}&refresh=true`;
  const withRefreshDetails = await api.requestJSON<CMxAPI.Case>(
    urlWithRefresh,
    {},
    true
  );
  let activeItemWithRefresh = withRefreshDetails.caseRuns.find(
    caseRun =>
      caseRun[processInstanceIdProp].toString() === match.params.id.toString()
  );
  if (!activeItemWithRefresh) {
    activeItemWithRefresh = withRefreshDetails.caseRuns[0];
  }
  dispatch(caseRunWithRefreshAvailable(activeItemWithRefresh));
};

const resolveActiveCaseRun = (
  withDetails: CMxAPI.Case,
  processInstanceIdProp: string,
  match: SimpleParams
) => {
  let activeItem = withDetails.caseRuns.find(
    caseRun =>
      caseRun[processInstanceIdProp].toString() === match.params.id.toString()
  );
  if (!activeItem) {
    activeItem = withDetails.caseRuns[0];
  }
  return activeItem;
};

const renderBenchFromCaseDetail = async (
  withDetails: CMxAPI.Case,
  processInstanceIdProp: string,
  match: SimpleParams,
  dispatch: LoadFromUrlDispatch
) => {
  const activeItem = resolveActiveCaseRun(withDetails, processInstanceIdProp, match);
  if (isIPC(activeItem.service_line)) {
    await dispatch(getDocumentsAsHtml(activeItem));
  }
  await loadCaseFromURL(activeItem, dispatch);
  return activeItem;
};

const loadFromURLSupportingData = async (
  activeItem: CMxAPI.CaseRun,
  dispatch: LoadFromUrlDispatch
) => {
  let processInstance;
  try {
    processInstance = unwrapResult(await dispatch(fetchProcessInstance(activeItem)));
  } catch (e) {
    // ignore
  }

  if (isIPC(activeItem.service_line) || isSurgery(activeItem.service_line)) {
    dispatch(
      caseEditsActions.loadAllPatientDetailOptions({
        activeTenantId: activeItem.tenant_uid,
        facility: activeItem.facility
      })
    );
  }

  await loadXCaliber(dispatch);

  try {
    if (isIPC(activeItem?.service_line)) {
      await dispatch(getFlagItems(activeItem as CMxAPI.CaseRunV2));
    }
  } catch (error) {
    console.error(`Error while fetching flags: ${error.message}`);
  }

  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line,
    caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
  });

  try {
    await dispatch(
      fetchImprovementOptions({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );
  } catch (error) {
    console.error(`Error while fetching improvement options: ${error.message}`);
  }

  try {
    await dispatch(
      fetchProviderRoleTypeConfigurations({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );
  } catch (error) {
    console.error(
      `Error while fetching provider role type configuration: ${error.message}`
    );
  }

  try {
    if (!isPathology(activeItem?.service_line)) {
      dispatch(
        getCCICustomClaimCrossCases({
          activeItem: activeItem,
          processInstance: processInstance
        })
      );
    }

    dispatch(
      fetchOrderTypeStatus({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );

    let filters;
    try {
      filters = unwrapResult(
        await dispatch(
          fetchRelateCaseFilterDictionaries({
            activeItem: activeItem,
            processInstance: processInstance
          })
        )
      );
    } catch (e) {
      // ignore
    }
    let startDate: Date;
    let endDate: Date;
    if (activeItem.admission_datetime && activeItem.discharge_datetime) {
      startDate = activeItem.admission_datetime;
      endDate = activeItem.discharge_datetime;
    } else {
      startDate = cmxDateTime.format(
        new Date(),
        cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE,
        {
          throw: false,
          localized: true,
          offset: {
            action: cmxDateTime.DateManipulateOperation.SUBTRACT,
            delta: [30, cmxDateTime.DateManipulateUnit.DAY]
          }
        }
      );
      endDate = new Date();
    }

    const filterSelection = getRelatedCasesInitialFilters(
      filters,
      startDate,
      endDate
    );

    if (activeItem?.encounter_identifier?.number) {
      if (!isPathology(activeItem.service_line)) {
        dispatch(
          getCasesWithEncounter({
            encounterId: activeItem?.encounter_identifier?.number,
            sortablePageable: { ...relatedCasesSortablePageable },
            serviceLine: serviceLineConfig.amplifyPath,
            filterSelection
          })
        );
      }
      dispatch(
        fetchRelatedCodes({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'ICD',
          caseId: activeItem.case_id
        })
      );
      dispatch(
        fetchRelatedCodes({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'CPT',
          caseId: activeItem.case_id
        })
      );
      dispatch(
        fetchRelatedCodes({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'MODIFIER',
          caseId: activeItem.case_id
        })
      );
    }
  } catch (error) {
    console.error(`Error while fetching related cases: ${error.message}`);
  }
};

const loadFromURLMatch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  SimpleParams,
  { rejectValue: CMxCommonApp.CMxError; state: LoadFromUrlState }
>(Launch.LOAD_FROM_URL, async (match, { dispatch, getState, rejectWithValue }) => {
  const state = getState() as any;

  // CDMX-17209: A logout teardown can transiently re-mount the bench and fire
  // this load. The login reducer clears auth state on LOGOUT before that mount,
  // so an empty Redux token means we are tearing down. Bail before any network
  // call to avoid a doomed GET /contexts/v1 (403) and the resulting RUM noise.
  // authHandler.token is unreliable here (still set mid-logout), so key off state.
  if (!getStateAuthToken(state)) {
    return rejectWithValue({
      name: 'UnauthenticatedError',
      message:
        'No active session; skipping LOAD_FROM_URL during logout teardown',
      errors: {}
    } as unknown as CMxCommonApp.CMxError);
  }

  syncAuthTokenFromState(state);

  const loadedContext = getLoadedContext(state);
  const contextJSON: CMxAPI.ContextAPI = loadedContext ?? (await fetchContextJSON());
  const shimmedContext = loadedContext ?? shimContext(contextJSON);
  dispatch(userDetailsAvailable(contextJSON));

  // Check feature flag for display_all_case_runs
  const useV3Endpoint = shimmedContext.activeContext
    ? isResourceAllowed(
        shimmedContext.activeContext,
        commonEnums.ResourceId.DISPLAY_ALL_CASE_RUNS,
        match.params.serviceLine
      )
    : false;

  const sl = appServiceLines.determineServiceLineConfig({
    name: match.params.serviceLine,
    displayAllCaseRuns: useV3Endpoint
  });

  const processInstanceIdProp = appServiceLines.determineProcessInstanceId({
    name: match.params.serviceLine
  });

  // Use utility function to build URL
  const url = buildCaseDetailUrl(
    match.params.caseId,
    sl
  );

  // Match getDetail: Case.DETAIL cancels Phase 1 / fallback / flag-off single fetch;
  // Case.DETAIL_RECONCILE cancels Phase 2. A newer loadFromURLMatch calls both
  // getSignal keys, so an in-flight open cannot resolve after the user has moved on.
  const detailSignal = abortController.getSignal(Case.DETAIL);
  const reconcileSignal = abortController.getSignal(Case.DETAIL_RECONCILE);
  const detailInit: RequestInit = { ...REQUEST_INIT_OPTIONS, signal: detailSignal };
  const reconcileInit: RequestInit = {
    ...REQUEST_INIT_OPTIONS,
    signal: reconcileSignal
  };
  let phase1Rendered = false;
  let phase1SupportingDataPromise: Promise<void> | undefined;
  const ipcServiceLine = isIPC(match.params.serviceLine);
  const phase2Url = ipcServiceLine ? appendCaseDetailRefreshParam(url) : url;

  const fetchResult = await orchestrateTwoPhaseCaseDetailFetch({
    single: async () => {
      const data = await api.requestJSON<CMxAPI.Case>(url, detailInit, true);
      return { data, xCache: undefined };
    },
    phase1: () => requestJSONWithCacheMeta<CMxAPI.Case>(url, detailInit, true),
    phase2: () =>
      requestJSONWithCacheMeta<CMxAPI.Case>(phase2Url, reconcileInit, false),
    fallback: () =>
      requestJSONWithCacheMeta<CMxAPI.Case>(url, detailInit, false),
    onPhase1Complete: async (data, xCache) => {
      if (xCache === 'HIT') {
        await renderBenchFromCaseDetail(
          data,
          processInstanceIdProp,
          match,
          dispatch
        );
        phase1Rendered = true;
        const activeItem = resolveActiveCaseRun(
          data,
          processInstanceIdProp,
          match
        );
        // Kick off process instance and other supporting fetches in parallel with
        // Phase 2 reconcile — do not await here so authoritative fetch is not blocked.
        phase1SupportingDataPromise = loadFromURLSupportingData(
          activeItem,
          dispatch
        );
      }
    },
    differs: (phase1, phase2) =>
      runCaseDetailDiff(
        phase1,
        phase2,
        {
          processId: match.params.id,
          phase2UsedRefresh: phase2Url !== url
        },
        reconcileSignal
      )
  });

  const withDetails = fetchResult.data;

  // Phase 1 may have already painted a cache HIT. Re-render when Phase 2 was auto-applied so the
  // bench shows the fresher payload, not the stashed Phase 1 shell.
  if (!phase1Rendered || fetchResult.autoAppliedReconciliation) {
    await renderBenchFromCaseDetail(
      withDetails,
      processInstanceIdProp,
      match,
      dispatch
    );
  }

  if (fetchResult.autoAppliedReconciliation) {
    dispatch(
      caseDetailReconciliationApplied(fetchResult.reconciliationDiff ?? [])
    );
  } else if (fetchResult.freshnessWarning) {
    dispatch(caseDetailFreshnessWarning());
  }

  const skipSupportingData =
    fetchResult.deferSupportingData && fetchResult.freshnessWarning;

  const phase2IncludedXCaliberRefresh =
    isCaseDetailTwoPhaseEnabled() && ipcServiceLine && fetchResult.xCache === 'HIT';

  if (
    !!withDetails &&
    match.params?.getCaserunWithRefresh &&
    !phase2IncludedXCaliberRefresh
  ) {
    loadCaseRunWithRefresh(url, processInstanceIdProp, match, dispatch).catch(
      error => {
        console.error(
          'Error while trying to load case run with refresh ',
          error?.message
        );
      }
    );
  }

  if (phase2IncludedXCaliberRefresh && !fetchResult.freshnessWarning) {
    dispatch(
      caseRunWithRefreshAvailable(
        resolveActiveCaseRun(withDetails, processInstanceIdProp, match)
      )
    );
  }

  if (!skipSupportingData) {
    const activeItem = resolveActiveCaseRun(
      withDetails,
      processInstanceIdProp,
      match
    );
    if (fetchResult.autoAppliedReconciliation) {
      // Re-fetch when Phase 2 drift changed the active CaseRun or its fields.
      await loadFromURLSupportingData(activeItem, dispatch);
    } else if (phase1SupportingDataPromise) {
      await phase1SupportingDataPromise;
    } else {
      await loadFromURLSupportingData(activeItem, dispatch);
    }
  }

  return withDetails;
});

const standaloneLaunch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  URLSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(AmplifyAuthenticate.AUTOMATE, async (searchParams, { dispatch }) => {
  if (searchParams.has('channel')) {
    try {
      await authenticationChannel.ready;

      const params = {};
      // Context params can be passed in via a queryString
      [...searchParams.keys()].forEach((key: string) => {
        let value: any;

        // special case for processInstanceGuids which are an
        // array.
        if (key === 'processInstanceGuids') {
          value = searchParams.get(key).split(',');
        } else {
          value = searchParams.get(key);
        }
        params[key] = value;
      });
      dispatch(contextParams(params));

      const sl = appServiceLines.determineServiceLineConfig({
        name: searchParams.get('serviceLine'),
        caseIdentifier: `${searchParams.get('caseId')}`
      });
      appServiceLines.set(sl);

      const url = `${sl.path}/caseinstance/${searchParams.get(
        'caseId'
      )}/details/${
        sl.caseDetailVersion
      }?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;

      const withDetails = await api.requestJSON<CMxAPI.Case>(url);
      //
      const processInstanceGuid = searchParams.get('processInstanceGuid');
      let activeItem = withDetails.caseRuns.find(
        caseRun => caseRun.process_instance_guid === processInstanceGuid
      );
      if (!activeItem) {
        activeItem = withDetails.caseRuns[0];
      }

      const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();
      dispatch(userDetailsAvailable(contextJSON));

      const useGuid = searchParams.get('useGuid') ?? 'false';
      const caseLoadResult = await loadCase(activeItem, dispatch, useGuid);
      dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: caseLoadResult.url }));
      await loadXCaliber(dispatch);

      return withDetails;
    } catch (e) {
      dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    }
  }
});

export {
  epicToken,
  loadFromURLMatch,
  standaloneLaunch,
  hostCaseId,
  loadCaseFromId,
  tokenAvailable,
  contextParams,
  launchParamsAvailable,
  selectCase,
  userDetailsAvailable,
  caseRunAvailable,
  caseRunWithRefreshAvailable,
  caseSearchAvailable,
  caseLookupError,
  billingGroupAvailable,
  fetchCases
};
